import { useState } from "react";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Check, Network, ScrollText, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { OrgNode } from "@/views/exec/OrgNode";
import { Resolutions } from "@/views/exec/BoardVotes";

const eur = (v) => `€${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = (a) => (a >= 100 ? "text-emerald-700" : a >= 70 ? "text-amber-700" : "text-red-700");

export function OrgChart() { const { t } = useT();
  const q = useApi("/exec/org-chart");
  const d = q.data;
  if (!d) return null;
  return (
    <div className="border rounded-lg bg-card p-3 mb-4" data-testid="exec-org-chart">
      <div className="eyebrow flex items-center gap-1 mb-1">
        <Network className="h-3 w-3" />Org chart
        <span className="ml-auto normal-case tracking-normal font-sans text-xs text-muted-foreground">{d.headcount} people · {d.teams} teams</span>
      </div>
      {d.root ? <OrgNode n={d.root} depth={0} /> : <div className="text-xs text-muted-foreground">{t("lbl.no_leadership_team_yet")}</div>}
    </div>
  );
}

function ApprovalRow({ r, onDone }) { const { t } = useT();
  const [reason, setReason] = useState("");
  const act = async (action) => {
    try {
      const { data } = await api.post(`/finance/${r.model}/${r.id}/${action}`, { reason: reason || null });
      toast.success(action === "approve" ? `${r.ref} approved — ${data.state}` : `${r.ref} rejected`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <li data-testid={`ceo-approval-${r.id}`} className="px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
      <span className="font-mono font-medium">{r.ref}</span>
      <span>{r.partner}</span>
      <b>{eur(r.total)}</b>
      <span className="text-muted-foreground">{r.company} · CFO {r.cfo}{r.comment ? ` — “${r.comment}”` : ""}</span>
      <span className="ml-auto flex items-center gap-1">
        <Input data-testid={`ceo-approval-reason-${r.id}`} className="h-7 w-40 text-xs" placeholder="comment / reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Button size="sm" className="h-7 bg-emerald-700 hover:bg-emerald-800" data-testid={`ceo-approve-${r.id}`} onClick={() => act("approve")}><Check className="h-3 w-3 mr-1" />{t("lbl.approve")}</Button>
        <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700" data-testid={`ceo-reject-${r.id}`} disabled={!reason.trim()} onClick={() => act("reject")}><X className="h-3 w-3 mr-1" />{t("lbl.reject")}</Button>
      </span>
    </li>
  );
}

// Documents above the CEO threshold waiting for one-tap sign-off.
export function CeoApprovals() { const { t } = useT();
  const q = useApi("/exec/ceo/approvals");
  const rows = q.data || [];
  return (
    <div className="border rounded-lg bg-card mb-4" data-testid="exec-ceo-approvals">
      <div className="px-3 py-2 eyebrow flex items-center gap-1"><ScrollText className="h-3 w-3" />Awaiting your sign-off <span className="text-muted-foreground">({rows.length})</span></div>
      <ul className="divide-y">{rows.map((r) => <ApprovalRow key={r.id} r={r} onDone={q.reload} />)}</ul>
      {rows.length === 0 ? <div className="px-3 py-3 text-xs text-muted-foreground">Nothing waiting. Documents above the CEO threshold (Finance → Alerts & nudges) land here after CFO approval.</div> : null}
    </div>
  );
}

export function CeoBriefButton({ s }) { const { t } = useT();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/exec/ceo/brief");
      toast.success(`Brief posted to #${data.channel}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return <Button size="sm" variant="outline" className="h-7" data-testid="exec-ceo-brief-now" disabled={busy || !s.brief_channel_id} onClick={go}><Send className="h-3 w-3 mr-1" />{t("lbl.post_weekly_brief_now")}</Button>;
}

// Inline monthly target editor for a team row (department heads only).
// CEO: monthly board-pack e-mail (first Monday) to the CEO + board members.
export function BoardMail({ s, onSaved }) { const { t } = useT();
  const [emails, setEmails] = useState((s.board_emails || []).join(", ")); const [busy, setBusy] = useState(false);
  const save = async (patch) => { try { await api.put("/exec/ceo/settings", { ...s, board_emails: emails.split(/[,;\s]+/).filter(Boolean), ...patch }); toast.success(t("lbl.saved")); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  const sendNow = async () => { setBusy(true); try { const { data } = await api.post("/exec/ceo/board-mail/now"); toast.success(data.queued ? `Board pack queued to ${data.queued} recipient${data.queued === 1 ? "" : "s"}` : "No recipients — add board e-mails"); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return <div className="border rounded-lg bg-card p-3 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="exec-board-mail">
    <label className="flex items-center gap-2"><input type="checkbox" data-testid="exec-board-mail-enabled" checked={!!s.board_mail_enabled} onChange={(e) => save({ board_mail_enabled: e.target.checked })} />E-mail the board pack on the first Monday of each month to the CEO and</label>
    <input data-testid="exec-board-emails" className="border rounded px-2 py-1 bg-card min-w-[280px]" placeholder="board@example.com, chair@example.com" value={emails} onChange={(e) => setEmails(e.target.value)} onBlur={() => save({})} />
    <label className="flex items-center gap-1">mail language<select data-testid="exec-board-lang" className="border rounded px-1.5 py-1 bg-card" value={s.board_lang || ""} onChange={(e) => save({ board_lang: e.target.value || null })}><option value="">= CEO's</option><option value="en">{t("lbl.english")}</option><option value="sq">{t("lbl.shqip")}</option></select></label>
    <Button size="sm" variant="outline" className="h-7" data-testid="exec-board-mail-now" disabled={busy} onClick={sendNow}><Send className="h-3 w-3 mr-1" />Send last month's pack now</Button>
  </div>;
}


// CEO: past board-pack mailings with re-download.
export function BoardMailHistory({ v }) { const { t } = useT();
  const q = useApi("/exec/ceo/board-mail/history", [v]); const rows = q.data || []; const [res, setRes] = useState(null);
  const open = async (r) => { try { const res = await api.get(`/exec/ceo/board-mail/history/${r.id}.pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank", "noopener"); } catch (e) { toast.error(errMsg(e)); } };
  const revoke = async (r) => { try { await api.post(`/exec/ceo/board-mail/history/${r.id}/revoke`); toast.success(t("lbl.board_link_revoked")); q.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = (r) => { navigator.clipboard?.writeText(`${window.location.origin}${r.link}`); toast.success(t("lbl.board_link_copied")); };
  if (!rows.length) return null;
  return <div className="border rounded-lg bg-card p-3 mb-4 text-xs" data-testid="exec-board-mail-history"><div className="eyebrow mb-1">{t("lbl.board_mail_history")}</div>
    <ul className="divide-y">{rows.map((r) => <li key={r.id} className="py-1 flex flex-wrap items-center gap-2" data-testid={`exec-board-mail-row-${r.id}`}><b>{r.month}</b><span className="text-muted-foreground">{new Date(r.at).toLocaleString()} · {r.by}</span><span className="truncate max-w-[360px]">→ {r.recipients.join(", ")}</span>
      {r.link && !r.revoked && <span className="text-muted-foreground" data-testid={`exec-board-mail-views-${r.id}`}>{r.views} view{r.views === 1 ? "" : "s"}</span>}
      {r.link && (r.revoked ? <span className="text-red-700" data-testid={`exec-board-mail-revoked-${r.id}`}>link revoked</span> : <><button data-testid={`exec-board-mail-copy-${r.id}`} className="underline text-cyan-700" onClick={() => copy(r)}>copy link</button><button data-testid={`exec-board-mail-revoke-${r.id}`} className="underline text-red-700" onClick={() => revoke(r)}>revoke</button></>)}
      {r.has_pdf && <button data-testid={`exec-board-mail-pdf-${r.id}`} className="ml-auto underline text-cyan-700" onClick={() => open(r)}>PDF</button>}
      {r.tally && <span className="text-muted-foreground" data-testid={`exec-board-mail-tally-${r.id}`}>{r.tally.resolutions} resolution{r.tally.resolutions === 1 ? "" : "s"} · ✓ {r.tally.approve} ✗ {r.tally.object}</span>}
      {r.link && !r.revoked && <button data-testid={`exec-board-mail-resolutions-${r.id}`} className="underline" onClick={() => setRes(res === r.id ? null : r.id)}>{res === r.id ? "hide resolutions" : "resolutions"}</button>}
      {res === r.id && <Resolutions hid={r.id} />}</li>)}</ul></div>;
}


export function GoalCell({ dept, team, month, onSaved }) { const { t: tr } = useT();
  const [v, setV] = useState(team.target == null ? "" : String(team.target));
  const save = async () => {
    const t = v === "" ? null : Number(v);
    const current = team.target == null ? null : team.target;
    if (t === current) return;
    try {
      await api.put(`/exec/${dept}/goals`, { team_id: team.team_id, month, target: t });
      toast.success(t == null ? "Target cleared" : "Target saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <span className="flex items-center gap-1 justify-end">
      <Input type="number" data-testid={`exec-goal-${team.team_id}`} className="h-7 w-24 text-xs font-mono text-right" placeholder="target" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} />
      {team.attainment != null ? <span data-testid={`exec-attain-${team.team_id}`} className={`text-[11px] font-mono ${tone(team.attainment)}`}>{team.attainment}%</span> : null}
    </span>
  );
}

// Threshold alerts on cockpit KPIs → in-app/phone notification once a day while crossed.
export function KpiAlerts({ dept, kpis }) { const { t } = useT();
  const q = useApi(`/exec/${dept}/alerts`);
  const [key, setKey] = useState(kpis[0]?.key || "");
  const [op, setOp] = useState("lt");
  const [thr, setThr] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    try {
      await api.post(`/exec/${dept}/alerts`, { kpi_key: key, op, threshold: Number(thr) });
      toast.success(t("lbl.alert_saved"));
      setThr("");
      q.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (a) => {
    try {
      await api.delete(`/exec/${dept}/alerts/${a.id}`);
      q.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/exec/${dept}/alerts/check`);
      toast[data.kpi_alerts ? "warning" : "success"](data.kpi_alerts ? `${data.kpi_alerts} alert${data.kpi_alerts > 1 ? "s" : ""} fired — check your notifications` : "No KPI has crossed a threshold");
      q.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const rows = q.data || [];
  return (
    <div className="border rounded-lg bg-card p-3 mb-4 text-xs" data-testid="exec-kpi-alerts">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="eyebrow">{t("lbl.push_alerts")}</span>
        <span className="text-muted-foreground">notify me on my phone when…</span>
        <select data-testid="exec-alert-kpi" className="border rounded px-1.5 py-1 bg-card" value={key} onChange={(e) => setKey(e.target.value)}>{kpis.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}</select>
        <select data-testid="exec-alert-op" className="border rounded px-1.5 py-1 bg-card" value={op} onChange={(e) => setOp(e.target.value)}><option value="lt">drops below</option><option value="gt">rises above</option></select>
        <Input type="number" data-testid="exec-alert-threshold" className="h-7 w-28 font-mono" placeholder="threshold" value={thr} onChange={(e) => setThr(e.target.value)} />
        <Button size="sm" className="h-7" data-testid="exec-alert-add" disabled={!key || thr === ""} onClick={add}>Add</Button>
        <Button size="sm" variant="outline" className="h-7 ml-auto" data-testid="exec-alert-check" disabled={busy || !rows.length} onClick={check}>{t("lbl.check_now")}</Button>
      </div>
      <ul className="flex flex-wrap gap-2">
        {rows.map((a) => (
          <li key={a.id} data-testid={`exec-alert-${a.id}`} className={`border rounded-md px-2 py-1 flex items-center gap-2 ${a.last_fired ? "border-amber-300 bg-amber-50" : "bg-muted/30"}`}>
            <span><b>{a.label}</b> {a.op === "lt" ? "<" : ">"} {Number(a.threshold).toLocaleString("de-DE")}</span>
            {a.last_value != null ? <span className="text-muted-foreground">now {Number(a.last_value).toLocaleString("de-DE")}</span> : null}
            {a.last_fired ? <span className="text-amber-700">fired {new Date(a.last_fired).toLocaleDateString()}</span> : null}
            <button data-testid={`exec-alert-del-${a.id}`} className="text-muted-foreground hover:text-red-600" onClick={() => del(a)}>×</button>
          </li>
        ))}
      </ul>
      {rows.length === 0 ? <div className="text-muted-foreground">No alerts yet — e.g. “Open backlog rises above 50” or “Revenue drops below 100 000”.</div> : null}
    </div>
  );
}
